import { React, useState, useEffect } from "react";
import {
	Button,
	TextField,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
	useEditCategoryGroupMutation,
	useCategoriesQuery,
} from "../../../services/queryHooks";
import { validateTextField } from "../../../utils/validation";
import ColorDisplayAndPicker from "../../../components/common/ColorDisplayAndPicker";

export default function EditCategoryGroupModal({
	isOpen,
	onClose,
	categoryGroup,
}) {
	const [name, setName] = useState("");
	const [errorName, setErrorName] = useState("");
	const [color, setColor] = useState("");
	const { mutate, reset } = useEditCategoryGroupMutation();
	const { t } = useTranslation();

	const { data: categories } = useCategoriesQuery();

	useEffect(() => {
		if (isOpen && categoryGroup) {
			setName(
				categoryGroup.groupName || t(categoryGroup.groupTranslationKey)
			);
			setColor(categoryGroup.color);
		}
	}, [isOpen, categoryGroup, t]);

	const handleCloseModal = () => {
		reset();
		onClose();
	};

	const validateInputs = () => {
		let isValid = true;
		setErrorName("");

		const textError = validateTextField(name, t, {
			fieldName: t("categoryGroupName"),
		});

		if (textError) {
			setErrorName(textError);
			isValid = false;
		} else if (
			categories.some(
				(cat) =>
					cat._id !== categoryGroup._id &&
					cat.type === categoryGroup.type &&
					(
						cat.groupName || t(cat.groupTranslationKey)
					)?.toLowerCase() === name.trim().toLowerCase()
			)
		) {
			setErrorName(t("groupNameExistsError"));
			isValid = false;
		}

		return isValid;
	};

	const submitForm = (e) => {
		e.preventDefault();
		if (!validateInputs()) return;

		const nothingChanged =
			name.trim() ===
				(
					categoryGroup.groupName ||
					t(categoryGroup.groupTranslationKey)
				).trim() && color === categoryGroup.color;

		if (nothingChanged) {
			onClose();
			return;
		}

		mutate({
			categoryGroupId: categoryGroup._id,
			groupName: name,
			color: color,
		});
		handleCloseModal();
	};

	return (
		<Dialog open={isOpen} onClose={handleCloseModal} disableEnforceFocus>
			<DialogTitle>
				{t("editCategoryGroup") +
					": " +
					(categoryGroup.groupName
						? categoryGroup.groupName
						: t(categoryGroup.groupTranslationKey))}
			</DialogTitle>
			<form noValidate onSubmit={submitForm}>
				<DialogContent>
					<TextField
						autoFocus
						label={t("name")}
						value={name}
						onChange={(e) => setName(e.target.value)}
						type="text"
						fullWidth
						error={errorName ? true : false}
						helperText={errorName}
					/>
					<FormControl
						sx={{
							mt: 2,
							display: "flex",
							flexDirection: "row",
							alignItems: "baseline",
							gap: 1,
						}}
					>
						<Typography
							component="span"
							variant="subtitle1"
							gutterBottom
						>
							{t("pickCategoryGroupColor") + ":"}
						</Typography>
						<ColorDisplayAndPicker
							color={color}
							onColorChange={(newColor) => setColor(newColor)}
							isEditable={true}
						/>
					</FormControl>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseModal}>{t("cancel")}</Button>
					<Button
						type="submit"
						variant="contained"
						onClick={validateInputs}
					>
						{t("save")}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}
